import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Building2, ChevronLeft, ChevronRight, MessageSquare, RotateCw, Search } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { apiRequest, parseJsonResponse } from "@/lib/queryClient";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import type { BrandsCommentsOverview, CommentsOverviewFilter } from "@shared/brands-dashboard";

const PAGE_SIZE = 20;
const overviewKey = (uid: string, search: string, filter: CommentsOverviewFilter, page: number) =>
  ["brands-dashboard", uid, search, filter, page] as const;

async function getOverview(url: string): Promise<BrandsCommentsOverview> {
  return parseJsonResponse<BrandsCommentsOverview>(await apiRequest("GET", url));
}

function formatDate(value: string | null): string {
  if (!value) return "No activity";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "No activity";
  return new Intl.DateTimeFormat(undefined, {
    month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
  }).format(date);
}

export default function BrandsDashboardPage() {
  const { user, isLoading: authLoading } = useAuth();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<CommentsOverviewFilter>("all");
  const [page, setPage] = useState(1);
  const uid = user?.id ?? "anonymous";

  const overview = useQuery({
    queryKey: overviewKey(uid, search, filter, page),
    queryFn: () => getOverview(
      `/api/brands-dashboard/comments?search=${encodeURIComponent(search)}&filter=${filter}&page=${page}&pageSize=${PAGE_SIZE}`,
    ),
    enabled: user?.isMaster === true && !authLoading,
    retry: false,
  });

  if (authLoading) {
    return <div className="p-6 md:p-8"><Skeleton className="h-10 w-64" /></div>;
  }
  if (!user?.isMaster) {
    return (
      <div className="mx-auto max-w-6xl p-6 md:p-8">
        <Card data-testid="brands-dashboard-denied">
          <CardContent className="py-12 text-center">
            <h1 className="text-lg font-semibold">Master access required</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              This shared review workspace is available to master administrators only.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const rows = overview.data?.rows ?? [];
  const totalPages = Math.max(1, Math.ceil((overview.data?.total ?? 0) / PAGE_SIZE));
  const setFilterValue = (value: CommentsOverviewFilter) => {
    setFilter(value);
    setPage(1);
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6 md:p-8">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="text-brands-dashboard-title">
            Brands Dashboard
          </h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Comment activity across client brands
          </p>
        </div>
        <Badge variant="secondary">Rolling 7-day window</Badge>
      </header>

      <Card>
        <CardHeader className="gap-4 border-b sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="text-base">Comments Overview</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              Brand-level activity and review counts. Open a brand to view its comments.
            </p>
            {overview.data && (
              <p className="mt-1 text-xs text-muted-foreground">
                Reporting window: {formatDate(overview.data.windowStart)} – {formatDate(overview.data.asOf)}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <label className="relative block">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                aria-label="Search brands"
                placeholder="Search brands"
                maxLength={200}
                value={search}
                onChange={(event) => { setSearch(event.target.value); setPage(1); }}
                className="pl-9 sm:w-56"
              />
            </label>
            <div className="flex flex-wrap gap-1" aria-label="Comment filters">
              {([
                ["all", "All"],
                ["needs-review", "Needs review"],
                ["last-7-days", "Last 7 days"],
              ] as [CommentsOverviewFilter, string][]).map(([value, label]) => (
                <Button
                  key={value}
                  type="button"
                  size="sm"
                  variant={filter === value ? "secondary" : "ghost"}
                  aria-pressed={filter === value}
                  onClick={() => setFilterValue(value)}
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>

        {overview.isLoading ? (
          <CardContent className="space-y-3 p-6" aria-label="Loading brands">
            {Array.from({ length: 5 }, (_, index) => <Skeleton key={index} className="h-12 w-full" />)}
          </CardContent>
        ) : overview.isError ? (
          <CardContent className="py-12 text-center" role="alert">
            <p className="font-medium">
              {overview.error.message.startsWith("403:")
                ? "Master access required"
                : "Could not load the shared review queue."}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">Your saved statuses are unchanged.</p>
            <Button variant="outline" className="mt-4" onClick={() => void overview.refetch()}>
              <RotateCw className="mr-2 h-4 w-4" /> Retry
            </Button>
          </CardContent>
        ) : rows.length === 0 ? (
          <CardContent className="py-14 text-center">
            <MessageSquare className="mx-auto mb-3 h-6 w-6 text-muted-foreground" />
            <h2 className="font-semibold">Nothing in this view</h2>
            <p className="mt-1 text-sm text-muted-foreground">Try another filter or search term.</p>
            <Button variant="ghost" className="mt-2" onClick={() => {
              setSearch("");
              setFilterValue("all");
            }}>
              Clear filters
            </Button>
          </CardContent>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Brand</TableHead>
                    <TableHead className="text-right">Comments</TableHead>
                    <TableHead className="text-right">Unreviewed</TableHead>
                    <TableHead className="text-right">Last 7 days</TableHead>
                    <TableHead>Latest activity</TableHead>
                    <TableHead className="text-right">Comments</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.brandId} data-testid={`row-brand-${row.brandId}`}>
                      <TableCell className="min-w-56 font-medium">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-9 w-9 shrink-0 items-center justify-center rounded-md after:hidden">
                            <AvatarImage
                              src={row.brandLogoUrl ?? undefined}
                              alt=""
                              className="object-contain object-center"
                            />
                            <AvatarFallback className="rounded-md">
                              {row.brandName ? row.brandName.slice(0, 2).toUpperCase() : <Building2 className="h-4 w-4" />}
                            </AvatarFallback>
                          </Avatar>
                          <span>{row.brandName}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{row.totalCount}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        <Badge variant={row.unreviewedCount ? "destructive" : "secondary"}>
                          {row.unreviewedCount}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{row.recentCount}</TableCell>
                      <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                        {formatDate(row.latestCommentAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Link
                          href={`/brands/${encodeURIComponent(row.brandId)}/library?view=notes`}
                          className="inline-flex items-center font-medium text-primary hover:underline"
                          data-testid={`link-brand-comments-${row.brandId}`}
                        >
                          View comments
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <CardContent className="flex flex-wrap items-center justify-between gap-3 border-t py-4">
              <p className="text-sm text-muted-foreground">
                {overview.data?.total ?? 0} brands · showing {rows.length} on this page
              </p>
              <div className="flex items-center gap-2">
                <span className="text-sm tabular-nums text-muted-foreground">Page {page} of {totalPages}</span>
                <Button
                  size="sm"
                  variant="outline"
                  aria-label="Previous page"
                  disabled={page <= 1}
                  onClick={() => setPage((current) => current - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  aria-label="Next page"
                  disabled={page >= totalPages}
                  onClick={() => setPage((current) => current + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </>
        )}
      </Card>
    </div>
  );
}
